import React from 'react';
import { Card } from './ui/card';
import { features } from '../mock';
import { 
  FileText, 
  AlertCircle, 
  Users, 
  TrendingUp, 
  Target, 
  Brain 
} from 'lucide-react';

const iconMap = {
  FileText,
  AlertCircle,
  Users,
  TrendingUp,
  Target,
  Brain
};

const FeaturesSection = () => {
  return (
    <section className="py-24 bg-white">
      <div className="max-w-7xl mx-auto px-6">
        {/* Section header */}
        <div className="text-center mb-16 max-w-3xl mx-auto">
          <h2 className="text-4xl lg:text-5xl font-bold text-slate-900 mb-6">
            Your AI Chief of Staff
            <br />
            <span className="bg-gradient-to-r from-blue-600 to-teal-600 bg-clip-text text-transparent">
              That Never Sleeps
            </span>
          </h2>
          <p className="text-xl text-slate-600 leading-relaxed">
            Nucleus reads every commit, every PR, every ticket, every message. 
            It understands context and generates narrative intelligence on demand.
          </p>
        </div>

        {/* Features grid */}
        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8 mb-16">
          {features.map((feature, idx) => {
            const Icon = iconMap[feature.icon];
            return (
              <Card 
                key={idx}
                className="p-8 border-2 border-slate-200 hover:border-blue-300 hover:shadow-xl transition-all duration-300 group bg-white"
              >
                <div className="mb-5">
                  <div className="w-14 h-14 bg-gradient-to-br from-blue-100 to-teal-100 rounded-xl flex items-center justify-center group-hover:scale-110 transition-transform duration-300">
                    <Icon className="w-7 h-7 text-blue-700" />
                  </div>
                </div>
                <h3 className="text-xl font-bold text-slate-900 mb-3">
                  {feature.title}
                </h3>
                <p className="text-slate-600 leading-relaxed">
                  {feature.description}
                </p>
              </Card>
            );
          })}
        </div>

        {/* Visual showcase */}
        <div className="grid lg:grid-cols-2 gap-12 items-center">
          <div>
            <h3 className="text-3xl font-bold text-slate-900 mb-6">
              Engineering Intelligence, Delivered
            </h3>
            <div className="space-y-6">
              <div className="flex gap-4">
                <div className="flex-shrink-0 w-12 h-12 bg-blue-100 rounded-lg flex items-center justify-center">
                  <span className="text-xl font-bold text-blue-700">1</span>
                </div>
                <div>
                  <h4 className="font-bold text-slate-900 mb-2">Connect Your Tools</h4>
                  <p className="text-slate-600">
                    OAuth integration with Jira, GitHub, Slack, Gmail, Calendar, and WhatsApp. 
                    Takes 10 minutes.
                  </p>
                </div>
              </div>
              <div className="flex gap-4">
                <div className="flex-shrink-0 w-12 h-12 bg-teal-100 rounded-lg flex items-center justify-center">
                  <span className="text-xl font-bold text-teal-700">2</span>
                </div>
                <div>
                  <h4 className="font-bold text-slate-900 mb-2">AI Analyzes Everything</h4>
                  <p className="text-slate-600">
                    Nucleus reads and understands your commits, PRs, tickets, and messages. 
                    Learns your team's patterns.
                  </p>
                </div>
              </div>
              <div className="flex gap-4">
                <div className="flex-shrink-0 w-12 h-12 bg-blue-100 rounded-lg flex items-center justify-center">
                  <span className="text-xl font-bold text-blue-700">3</span>
                </div>
                <div>
                  <h4 className="font-bold text-slate-900 mb-2">Ask Anything, Get Insights</h4>
                  <p className="text-slate-600">
                    Natural language queries return instant, context-aware answers. 
                    No manual reporting ever again.
                  </p>
                </div>
              </div>
            </div>
          </div>

          <div className="relative">
            <div className="relative rounded-2xl overflow-hidden shadow-2xl border-2 border-slate-200">
              <img 
                src="https://images.unsplash.com/photo-1610563166150-b34df4f3bcd6" 
                alt="Development Environment"
                className="w-full h-auto object-cover"
              />
              <div className="absolute inset-0 bg-gradient-to-t from-slate-900/60 to-transparent"></div>
            </div>
            {/* Decorative element */}
            <div className="absolute -bottom-6 -right-6 w-32 h-32 bg-teal-200 rounded-full opacity-30 blur-2xl"></div>
            <div className="absolute -top-6 -left-6 w-32 h-32 bg-blue-200 rounded-full opacity-30 blur-2xl"></div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default FeaturesSection;
